import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { useToast } from "@/hooks/use-toast";
import { useAllApps } from "@/hooks/apps";
import { groupAppsByGroup } from "@/constants/app-groups";
import type { App } from "@/types/domain";
import { CheckIcon } from "lucide-react";
import { putFetcher, swrKey } from "@/lib/swr";
import type { User } from "@/lib/swr";
import { useMemo, useState } from "react";
import { useSWRConfig } from "swr";
import useSWRMutation from "swr/mutation";

// Edit active App access without changing archived App assignments.
export const EditVisibleAppsDialog = ({ user }: { user: User }) => {
  const { toast } = useToast();
  const { mutate } = useSWRConfig();
  const apps: App[] = useAllApps();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<string[]>(
    (user?.visibleApps || []).map((a) => String(a.id))
  );
  const activeIds = new Set(apps.map((app) => String(app.id)));
  // Hidden assignments are retained when changing the active App selection.
  const hiddenAssigned = (user.visibleApps || []).map((app) => String(app.id)).filter((id) => !activeIds.has(id));

  const { trigger } = useSWRMutation(`/users/${user.id}`, putFetcher);

  const handleSave = async () => {
    trigger(
      { visibleAppIds: [...new Set([...selected.filter((id) => activeIds.has(id)), ...hiddenAssigned])] },
      {
        onSuccess: () => {
          mutate(swrKey("/users/full"));
          toast({ title: "Update success", description: "User visible Apps updated" });
          setOpen(false);
        },
        onError: (err) => {
          toast({ title: "Update failed", description: err?.message });
        },
      }
    );
  };

  const groups = useMemo(() => groupAppsByGroup(apps), [apps]);

  const toggle = (id: string) => {
    setSelected((cur) =>
      cur.includes(id) ? cur.filter((x) => x !== id) : [...cur, id]
    );
  };

  // Select the whole group, or deselect it if every app in it is already selected.
  const toggleGroup = (groupApps: App[]) => {
    const ids = groupApps.map((a) => a.id);
    setSelected((cur) =>
      ids.every((id) => cur.includes(id))
        ? cur.filter((id) => !ids.includes(id))
        : [...new Set([...cur, ...ids])]
    );
  };

  return (
    <Dialog open={open} onOpenChange={(next) => {
      if (next) setSelected((user.visibleApps || []).map((app) => String(app.id)));
      setOpen(next);
    }}>
      <DialogTrigger asChild>
        <Button variant="outline" size="sm">
          Visible Apps
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Edit active App access</DialogTitle>
          <DialogDescription>
            Choose the active Apps this user can access. Archived App access is retained; change it in Admin → Apps → Archived → Members.
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-4">
          <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-muted-foreground">
            <span>Active Apps: {selected.filter((id) => activeIds.has(id)).length} / {apps.length} selected</span>
            <div className="flex gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelected((current) => [...new Set([...current, ...apps.map((a) => a.id)])])}
              >
                Select all active
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSelected((current) => current.filter((id) => !activeIds.has(id)))}
              >
                Clear active
              </Button>
            </div>
          </div>
          <Command className="border rounded-lg">
            <CommandInput
              placeholder="Search app…"
              value={query}
              onValueChange={setQuery}
            />
            <CommandEmpty>No results.</CommandEmpty>
            <CommandList>
              {groups.map(([groupName, groupApps]) => {
                const selectedCount = groupApps.filter((a) =>
                  selected.includes(a.id)
                ).length;
                const allSelected = selectedCount === groupApps.length;
                return (
                  <CommandGroup key={groupName} value={groupName}>
                    {/* Header lives inside the group (so it hides with it when
                        filtered out) but NOT in cmdk's aria-hidden heading. */}
                    <div className="flex items-center justify-between px-2 py-1.5 text-xs font-medium text-muted-foreground">
                      <span>
                        {groupName}
                        <span className="ml-2 tabular-nums">
                          {selectedCount} / {groupApps.length}
                        </span>
                      </span>
                      {/* Hidden while searching: it would toggle filtered-out
                          apps too — surprising in a permissions dialog. */}
                      {!query && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-5 px-1.5 text-xs"
                          onMouseDown={(e) => e.preventDefault()}
                          onKeyDown={(e) => {
                            // Keep Enter/Space from reaching cmdk's root, which
                            // would hijack them to select the highlighted item.
                            if (e.key === "Enter" || e.key === " ")
                              e.stopPropagation();
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleGroup(groupApps);
                          }}
                        >
                          {allSelected ? "Deselect group" : "Select group"}
                        </Button>
                      )}
                    </div>
                    {groupApps.map((app) => (
                      <CommandItem
                        key={app.id}
                        value={`${app.name} ${app.code ?? ""} ${app.id}`}
                        onSelect={() => toggle(app.id)}
                        className="flex items-center justify-between"
                      >
                        <span>
                          {app.name}
                          {app.code ? (
                            <span className="ml-2 text-muted-foreground">
                              {app.code}
                            </span>
                          ) : null}
                        </span>
                        {selected.includes(app.id) && (
                          <CheckIcon className="w-4 h-4" />
                        )}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                );
              })}
            </CommandList>
          </Command>
          <Button onClick={handleSave}>Save</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
